import { useCallback, useState } from "react";

/**
 * Hook generico per colonne ridimensionabili con larghezze persistite in
 * localStorage (2026-07-02). Nato per la pagina Messaggi, generalizzato per
 * la pagina Mail (proposta A): ogni pagina definisce chiavi, default, bounds
 * e storage key propri e compone il proprio grid-template.
 *
 * Persistenza in localStorage: preferenza di layout per-postazione, non un
 * dato di dominio → niente DB.
 */
export interface PanelWidthsConfig<K extends string> {
  storageKey: string;
  defaults: Record<K, number>;
  /** Clamp anti-layout-rotto: sotto il min i contenuti troncano male, oltre
   *  il max la colonna fluida scende sotto la larghezza utile. */
  bounds: Record<K, { min: number; max: number }>;
}

/** Larghezza dei divisori-handle in px (colonne di griglia dedicate). */
export const PANEL_DIVIDER_PX = 4;

function clampWidth<K extends string>(config: PanelWidthsConfig<K>, key: K, px: number): number {
  const { min, max } = config.bounds[key];
  return Math.min(max, Math.max(min, Math.round(px)));
}

function loadSavedWidths<K extends string>(config: PanelWidthsConfig<K>): Record<K, number> {
  const fallback: Record<K, number> = { ...config.defaults };
  try {
    const raw = window.localStorage.getItem(config.storageKey);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return fallback;
    const source = parsed as Record<string, unknown>;
    const result = { ...fallback };
    for (const key of Object.keys(config.defaults) as K[]) {
      const value = source[key];
      if (typeof value === "number" && Number.isFinite(value)) {
        result[key] = clampWidth(config, key, value);
      }
    }
    return result;
  } catch {
    // Storage corrotto/inaccessibile (private mode): layout ai default.
    return fallback;
  }
}

function persistWidths<K extends string>(
  config: PanelWidthsConfig<K>,
  widths: Record<K, number>,
): void {
  try {
    window.localStorage.setItem(config.storageKey, JSON.stringify(widths));
  } catch {
    // Best-effort: senza storage il resize funziona ma non sopravvive al reload.
  }
}

export function usePanelWidths<K extends string>(
  config: PanelWidthsConfig<K>,
): {
  widths: Record<K, number>;
  setWidth: (key: K, px: number) => void;
  resetWidth: (key: K) => void;
} {
  const [widths, setWidths] = useState<Record<K, number>>(() => loadSavedWidths(config));

  // ponytail: persist sincrono a ogni set (stringa minuscola); se il drag
  // dovesse mai stutterare, spostare la persist su pointerup.
  const setWidth = useCallback(
    (key: K, px: number) => {
      setWidths((current) => {
        const next = { ...current, [key]: clampWidth(config, key, px) };
        persistWidths(config, next);
        return next;
      });
    },
    [config],
  );

  const resetWidth = useCallback(
    (key: K) => {
      setWidths((current) => {
        const next = { ...current, [key]: config.defaults[key] };
        persistWidths(config, next);
        return next;
      });
    },
    [config],
  );

  return { widths, setWidth, resetWidth };
}
